(() => {
  if (customElements.get('filters-component')) {
    return;
  }

  class FiltersComponent extends HTMLElement {
    constructor() {
      super();
      this.debouncedOnSubmit = debounce(event => {
        this.onChange(event);
      }, 300);

      this.spinner = document.getElementById('spinner');
      this.gridObserver = null;
    }

    connectedCallback() {
      this.bindEvents();

      if (this.hasAttribute('open-desktop')) {
        this.expandedDesktop();

        window.addEventListener('resize', () => {
          this.expandedDesktop();
        });
      }

      const gridContainer = this.getGridContainer();
      if (gridContainer) {
        this.shuffleShopAllProducts(gridContainer);
        this.applyBestSellerSort(gridContainer);
        this.setupGridObserver(gridContainer);
      }
    }

    shuffleShopAllProducts(gridContainer) {
      if (!gridContainer) return;

      const isShopAllPage =
        window.location.pathname.toLowerCase().includes('/collections/shop-all') ||
        window.location.pathname.toLowerCase().includes('/collections/all');
      if (!isShopAllPage) return;

      const urlParams = new URLSearchParams(window.location.search);
      const hasSortOrFilter =
        urlParams.has('sort_by') || Array.from(urlParams.keys()).some(k => k.startsWith('filter.'));
      if (hasSortOrFilter) return;

      let cards = Array.from(gridContainer.querySelectorAll('card-product'));
      if (cards.length <= 1) return;

      // Group cards by primary collection or category
      const categoriesMap = new Map();

      cards.forEach(card => {
        let catKey = card.getAttribute('data-category-name') || '';
        if (!catKey) {
          const collectionsStr = card.getAttribute('data-collections') || '';
          const colList = collectionsStr.split(',').filter(c => c && c !== 'all' && c !== 'shop-all' && c !== 'frontpage');
          catKey = colList[0] || 'default';
        }

        if (!categoriesMap.has(catKey)) {
          categoriesMap.set(catKey, []);
        }
        categoriesMap.get(catKey).push(card);
      });

      // Randomly shuffle cards inside each category bucket
      const buckets = Array.from(categoriesMap.values()).map(bucket => {
        for (let i = bucket.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [bucket[i], bucket[j]] = [bucket[j], bucket[i]];
        }
        return bucket;
      });

      // Randomly shuffle the order of category buckets
      for (let i = buckets.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [buckets[i], buckets[j]] = [buckets[j], buckets[i]];
      }

      // Interleave items across buckets round-robin so different collections alternate
      const shuffledCards = [];
      let itemsRemaining = true;

      while (itemsRemaining) {
        itemsRemaining = false;
        for (let b = 0; b < buckets.length; b++) {
          if (buckets[b].length > 0) {
            shuffledCards.push(buckets[b].shift());
            itemsRemaining = true;
          }
        }
      }

      // Re-render interleaved product cards cleanly
      const fragment = document.createDocumentFragment();
      shuffledCards.forEach(card => fragment.appendChild(card));

      gridContainer.innerHTML = '';
      gridContainer.appendChild(fragment);
    }

    getGridContainer() {
      return (
        document.getElementById('product-grid') ||
        document.querySelector('.collection__grid') ||
        document.getElementById('ProductGridContainer')
      );
    }

    setupGridObserver(gridContainer) {
      if (this.gridObserver) {
        this.gridObserver.disconnect();
      }

      if (!gridContainer) return;

      this.gridObserver = new MutationObserver(() => {
        this.gridObserver.disconnect();
        this.applyBestSellerSort(gridContainer);
        this.gridObserver.observe(gridContainer, { childList: true });
      });

      this.gridObserver.observe(gridContainer, { childList: true });
    }

    applyBestSellerSort(gridContainer) {
      if (!gridContainer) return;

      // On dedicated /collections/best-sellers collection page, display all Best Seller products by default
      const isBestSellersCollectionPage =
        window.location.pathname.toLowerCase().includes('/collections/best-sellers') ||
        window.location.pathname.toLowerCase().includes('/collections/best-selling');

      if (isBestSellersCollectionPage) return;

      const urlParams = new URLSearchParams(window.location.search);
      const sortSelect = this.querySelector('[data-filter-sort]');
      const currentSort = urlParams.get('sort_by') || sortSelect?.value || '';

      // ONLY filter cards if sort_by is explicitly 'best-selling'.
      // For default sort (Featured / Most Relevant / manual / empty), show ALL products in the collection!
      if (currentSort !== 'best-selling') {
        return;
      }

      let cards = Array.from(gridContainer.querySelectorAll('card-product'));
      if (cards.length === 0) return;

      // Filter to keep ONLY products marked as Best Seller
      let targetCards = cards.filter(
        card => card.getAttribute('data-is-bestseller') === 'true'
      );

      if (targetCards.length > 0) {
        const fragment = document.createDocumentFragment();
        targetCards.forEach(card => fragment.appendChild(card));

        gridContainer.innerHTML = '';
        gridContainer.appendChild(fragment);
      } else {
        gridContainer.innerHTML = `
          <div class="collection__not-found" style="width: 100%; text-align: center; padding: 40px 0;">
            <h2 class="h3 regular center">No Best Seller products found matching the selected filters.</h2>
          </div>
        `;
      }
    }

    bindEvents() {
      const form = this.querySelector('[data-form]');
      if (form) {
        form.removeEventListener('input', this.debouncedOnSubmit);
        form.addEventListener('input', this.debouncedOnSubmit);
      }

      const sortSelect = this.querySelector('[data-filter-sort]');
      if (sortSelect) {
        sortSelect.removeEventListener('input', this.debouncedOnSubmit);
        sortSelect.addEventListener('input', this.debouncedOnSubmit);
      }

      this.querySelector('[data-opener]')?.addEventListener('click', () => this.toggle());
      this.querySelector('[data-close]')?.addEventListener('click', () => this.toggle());
      this.querySelector('[data-button-mobile-apply]')?.addEventListener('click', () => this.toggle());
      this.querySelector('[data-clear]')?.addEventListener('click', () => this.clear());
      this.querySelector('[data-button-mobile-clear]')?.addEventListener('click', () => this.clear());

      this.querySelectorAll('[data-active]').forEach(e => {
        e.addEventListener('click', (event) => this.onRemove(event));
      });

      this.querySelectorAll('[data-group]').forEach(e => {
        e.addEventListener('click', (event) => this.groupToggle(event));
      });
    }

    expandedDesktop() {
      if (window.innerWidth > 767) {
        this.setAttribute('open', '');
      } else {
        this.removeAttribute('open');
      }
    }

    toggle() {
      this.toggleAttribute('open');
      document.body.classList.toggle('no-scroll-mobile');
    }

    groupToggle(event) {
      event.currentTarget.toggleAttribute('close');
    }

    onChange(event) {
      if (event) event.preventDefault();
      this.fetchAndRenderGrid();
    }

    onRemove(event) {
      if (event) event.preventDefault();
      const href = event.currentTarget.getAttribute('href');
      if (href) {
        const searchParams = href.includes('?') ? href.split('?')[1] : '';
        history.pushState({}, '', window.location.pathname + (searchParams ? '?' + searchParams : ''));
        this.fetchAndRenderGrid(searchParams);
      } else {
        this.clear();
      }
    }

    fetchAndRenderGrid(explicitSearchParams = null) {
      const sectionId = this.dataset.id || 'main';
      let searchParams = '';

      if (explicitSearchParams !== null) {
        searchParams = explicitSearchParams;
      } else {
        const form = this.querySelector('[data-form]');
        let searchParamsObj = new URLSearchParams();
        if (form) {
          const formData = new FormData(form);
          searchParamsObj = new URLSearchParams(formData);
        }

        const sortSelect = this.querySelector('[data-filter-sort]');
        if (sortSelect && sortSelect.value) {
          searchParamsObj.set('sort_by', sortSelect.value);
        }

        // Replace '+' with '%20' so space-containing filter values match Shopify's section rendering backend
        searchParams = searchParamsObj.toString().replace(/\+/g, '%20');
      }

      if (explicitSearchParams === null) {
        const newUrl = window.location.pathname + (searchParams ? '?' + searchParams : '');
        history.pushState({}, '', newUrl);
      }

      if (typeof spinnerLoader !== 'undefined' && spinnerLoader?.enable) {
        spinnerLoader.enable(this.spinner);
      }

      const finishLoading = () => {
        if (typeof spinnerLoader !== 'undefined' && spinnerLoader?.disable) {
          spinnerLoader.disable(this.spinner);
        }
      };

      const endpoint = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
      fetch(endpoint)
        .then(res => res.text())
        .then(text => {
          const parsedDoc = new DOMParser().parseFromString(text, 'text/html');
          const newWrapper = parsedDoc.querySelector('.filter-wrapper');
          const currentWrapper = this.querySelector('.filter-wrapper');

          if (newWrapper && currentWrapper) {
            currentWrapper.innerHTML = newWrapper.innerHTML;
            this.bindEvents();
          } else {
            const gridContainer = this.getGridContainer();
            const newGrid =
              parsedDoc.getElementById('product-grid') ||
              parsedDoc.querySelector('.collection__grid') ||
              parsedDoc.getElementById('ProductGridContainer');
            if (gridContainer && newGrid) {
              gridContainer.innerHTML = newGrid.innerHTML;
            }
          }

          const updatedGrid = this.getGridContainer();
          if (updatedGrid) {
            this.applyBestSellerSort(updatedGrid);
            this.setupGridObserver(updatedGrid);
          }
        })
        .catch(err => console.error('Filter fetch error:', err))
        .finally(finishLoading);
    }

    renderHtml(searchParams) {
      this.fetchAndRenderGrid(searchParams);
    }

    clear() {
      history.pushState({}, '', window.location.pathname);
      this.fetchAndRenderGrid('');
    }
  }

  customElements.define('filters-component', FiltersComponent);
})();
